import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import PlayerCard from '../components/PlayerCard';
import Footer from '../components/Footer';
import { publicApi } from '../lib/api';
import { Users, Shield, Swords, Target, UserCog } from 'lucide-react';

const positions = [
  { key: 'Alle', label: 'Alle', icon: Users },
  { key: 'Torwart', label: 'Torwart', icon: Shield },
  { key: 'Verteidiger', label: 'Verteidigung', icon: Target },
  { key: 'Stürmer', label: 'Sturm', icon: Swords },
  { key: 'Staff', label: 'Staff', icon: UserCog },
];

export default function KaderPage() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('Alle');

  useEffect(() => {
    publicApi.getPlayers().then(res => {
      setPlayers(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const staffPositions = ['Trainer', 'Betreuer', 'Staff'];
  const filteredPlayers = activeFilter === 'Alle'
    ? players
    : activeFilter === 'Staff'
    ? players.filter(p => staffPositions.includes(p.position))
    : players.filter(p => p.position === activeFilter);

  // Group by position for section headers
  const groupedByPosition = {};
  if (activeFilter === 'Alle') {
    ['Torwart', 'Verteidiger', 'Stürmer'].forEach(pos => {
      const posPlayers = players.filter(p => p.position === pos);
      if (posPlayers.length > 0) groupedByPosition[pos] = posPlayers;
    });
    // Staff group
    const staffPlayers = players.filter(p => staffPositions.includes(p.position));
    if (staffPlayers.length > 0) groupedByPosition['Staff'] = staffPlayers;
    // Any others
    const allKnown = ['Torwart', 'Verteidiger', 'Stürmer', ...staffPositions];
    const others = players.filter(p => !allKnown.includes(p.position));
    if (others.length > 0) groupedByPosition['Sonstige'] = others;
  }

  const showGrouped = activeFilter === 'Alle' && Object.keys(groupedByPosition).length > 0;

  return (
    <div className="min-h-screen bg-[hsl(0,0%,5%)]">
      <Navbar />
      
      {/* Header */}
      <section className="pt-24 pb-8 relative overflow-hidden">
        {/* Subtle background accent */}
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(600px 300px at 10% 50%, rgba(204,0,0,0.06) 0%, transparent 60%)'
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs text-[#46C7FF] uppercase tracking-[0.2em] font-semibold">Mannschaft</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3 mb-4" style={{ fontFamily: 'Space Grotesk' }}>
              Unser <span className="text-[#CC0000]">Kader</span>
            </h1>
            <p className="text-white/50 max-w-lg text-base leading-relaxed">
              Die Spieler der Dresden Devils – mit Leidenschaft und Einsatz auf dem Eis.
            </p>
          </motion.div>

          {/* Filter Bar */}
          <div className="mt-8 flex flex-wrap gap-2" data-testid="player-filter-toggle-group">
            {positions.map((pos) => {
              const Icon = pos.icon;
              const count = pos.key === 'Alle' ? players.length : pos.key === 'Staff' ? players.filter(p => staffPositions.includes(p.position)).length : players.filter(p => p.position === pos.key).length;
              return (
                <button
                  key={pos.key}
                  onClick={() => setActiveFilter(pos.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg font-medium transition-colors duration-200 ${
                    activeFilter === pos.key
                      ? 'bg-[#CC0000] text-white shadow-lg shadow-[#CC0000]/20'
                      : 'bg-[hsl(0,0%,8%)] text-white/50 hover:text-white/70 hover:bg-[hsl(0,0%,10%)] border border-white/8'
                  }`}
                  data-testid={`player-filter-${pos.key.toLowerCase()}`}
                >
                  <Icon className="w-4 h-4" />
                  {pos.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeFilter === pos.key ? 'bg-white/20' : 'bg-white/5'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Players Grid */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl animate-pulse">
                  <div className="aspect-[3/4]" />
                  <div className="p-4">
                    <div className="h-4 bg-white/5 rounded w-2/3 mb-2" />
                    <div className="h-3 bg-white/5 rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPlayers.length === 0 ? (
            <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-20 text-center">
              <Users className="w-14 h-14 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-base mb-2">
                {activeFilter === 'Alle'
                  ? 'Noch keine Spieler hinzugefügt'
                  : `Keine Spieler in der Position "${activeFilter}"`}
              </p>
              <p className="text-white/20 text-xs">Spieler können im Admin-Bereich verwaltet werden</p>
            </div>
          ) : showGrouped ? (
            // Grouped by position
            <div className="space-y-12">
              {Object.entries(groupedByPosition).map(([posName, posPlayers]) => (
                <div key={posName}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-px flex-1 bg-white/5" />
                    <span className="text-xs text-white/30 uppercase tracking-[0.2em] font-bold" style={{ fontFamily: 'Space Grotesk' }}>
                      {posName === 'Torwart' ? 'Torhüter' : posName}
                    </span>
                    <span className="text-xs text-white/15 font-mono-score">{posPlayers.length}</span>
                    <div className="h-px flex-1 bg-white/5" />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                    {posPlayers.map((player, index) => (
                      <PlayerCard key={player._id} player={player} index={index} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Flat grid for filtered view
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {filteredPlayers.map((player, index) => (
                <PlayerCard key={player._id} player={player} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
